import { FC, useState } from 'react'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import CityAutocompleteField from '../../../../modules/auth/components/fields/CityAutocompleteField'

interface Props {
	isOpen: boolean
	onClose: () => void
	onSubmit: (data: any) => void
	paymentData?: any
}

export interface BankAccountFormValues {
	id?: number | string
	type_id: string
	beneficiary: string
	is_primary: boolean
	bank_name: string
	swift_code: string
	iban?: string
	street_address: string
	apt: string
	city: string
	state: string
	zip_code: string
}

export const normalizeBankAccount = (data: any = {}): BankAccountFormValues => ({
	...(data?.id ? { id: data.id } : {}),
	type_id: String(data?.typeId ?? data?.type_id ?? '').trim(),
	beneficiary: String(data?.beneficiary ?? '').trim(),
	is_primary: Boolean(data?.isPrimary ?? data?.is_primary ?? false),
	bank_name: String(data?.bank_name ?? data?.bankName ?? '').trim(),
	swift_code: String(data?.swift_code ?? data?.swiftCode ?? '').trim(),
	iban: String(data?.iban ?? '').trim(),
	street_address: String(data?.street_address ?? data?.streetAddress ?? '').trim(),
	apt: String(data?.apt ?? data?.apt_suite ?? data?.aptSuite ?? '').trim(),
	city: String(data?.city ?? '').trim(),
	state: String(data?.state ?? '').trim(),
	zip_code: String(data?.zip_code ?? data?.zipCode ?? '').trim(),
})

export const isBankAccountComplete = (data: any) => {
	const normalized = normalizeBankAccount(data)
	const requiredKeys: (keyof BankAccountFormValues)[] = [
		'type_id', 'beneficiary', 'bank_name', 'swift_code', 'street_address', 'apt', 'city', 'state', 'zip_code'
	]
	return requiredKeys.every((key) => normalized[key] !== '')
}

const PaymentMethodModal: FC<Props> = ({
	isOpen,
	onClose,
	onSubmit,
	paymentData
}) => {
	const [loading, setLoading] = useState<boolean>(false);
	const types = [
        { id: 'Commercial', name: 'Commercial' },
        { id: 'Impact/Philanthropy', name: 'Impact / Philanthropy' }
    ]

	if (!isOpen) return null

	const initialValues = normalizeBankAccount(paymentData)

	return (
		<div className='nb-modal-overlay' onClick={onClose}>
			<div className='nb-invite-modal' onClick={(e) => e.stopPropagation()}>
				{/* CLOSE BUTTON */}
				<button className='nb-invite-modal__close' onClick={onClose}>
					<svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
						<circle cx='12' cy='12' r='10' stroke='#808080' strokeWidth='1' />
						<path d='M9.17 9.17l5.66 5.66M14.83 9.17l-5.66 5.66' stroke='#808080' strokeWidth='1' />
					</svg>
				</button>

				{/* TITLE */}
				<h2 className='nb-invite-modal__title'>
					{paymentData?.bank_name || paymentData?.bankName || paymentData?.id ? 'Edit Payout Method' : 'Add Payout Method'}
				</h2>

				<Formik
					enableReinitialize
					initialValues={initialValues}
					validationSchema={Yup.object({
						type_id: Yup.string().required('This field is required'),
						beneficiary: Yup.string().required('This field is required').max(100, "Max 100 characters"),
						is_primary: Yup.boolean(),
						bank_name: Yup.string().trim().required('This field is required').max(100, "Max 100 characters"),
						swift_code: Yup.string().trim().required('This field is required').max(100, "Max 100 characters"),
						street_address: Yup.string().trim().required('This field is required').max(100, "Max 100 characters"),
						apt: Yup.string().trim().required('This field is required').max(100, "Max 100 characters"),
						city: Yup.string().trim().required('This field is required').max(100, "Max 100 characters"),
						state: Yup.string().trim().required('This field is required').max(100, "Max 100 characters"),
						zip_code: Yup.string().trim().required('This field is required').max(10, "Max 10 characters"),
					})}
					onSubmit={async (values) => {
						try {
							setLoading(true)
							try {
								onSubmit(normalizeBankAccount({ ...values, id: paymentData?.id }))
								onClose()
							} finally {
								setLoading(false)
							}
						} catch (error: any) {
							setLoading(false)
						}
					}}
				>
					{({values, handleChange}) => {
						return (
							<Form className='profile-admin-form  profile-form-base' style={{ display: 'contents' }}>
								<div className="w-100">
									<div className='tap-add-experience-2-radio-row'>
										{types.map((type: any, index: number) => {
											return (<div key={`type-payout-${index}`} className={`tap-add-experience-2-radio-card ${values?.type_id == type.id ? 'tap-add-experience-2-radio-card--selected' : ''}`}>
												<label className="tap-host-option p-6 w-100">
													<input type="radio" className="tap-host-radio-input"
														name="type_id"
														value={type.id}
														checked={values?.type_id === type.id}
														onChange={handleChange}
													/>
													<div className="tap-host-radio">
														<div className="tap-host-radio-outer"></div>
														<div className="tap-host-radio-inner"></div>
													</div>
													<div className="tap-host-option-label">{type.name}</div>
												</label>
											</div>)
										})}
										<ErrorMessage name='type_id' component="div" className="tap-add-experience-text text-danger" />
									</div>

									<div className='pf-checkbox mt-6'>
										<input type="checkbox" className="pf-check" checked={values?.is_primary} onChange={handleChange} name="is_primary"/>
										<label htmlFor="assignment-1" className="pf-checkbox-content">
											<div className="assignment-title">Is primary</div>
										</label>
									</div>

									<div className="tap-add-experience-2-section mt-6 gap-1">
										<span className="team-admin-add-member__section-title">Beneficiary</span>
										<div className='form-field team-admin-add-member__card'>
											<label className='field-label'>Name</label>
											<Field name='beneficiary' className='input' />
											<ErrorMessage name='beneficiary' component="div" className="tap-add-experience-text text-danger" />
										</div>
									</div>

									<div className="tap-add-experience-2-section mt-6 gap-1">
										<span className="team-admin-add-member__section-title">Bank account</span>
										<div className='form-field team-admin-add-member__card'>
											<div className='w-100'>
												<label className='field-label'>Bank Name</label>
												<Field name='bank_name' className='input' />
												<ErrorMessage name='bank_name' component="div" className="tap-add-experience-text text-danger" />
											</div>
											<div className="w-100">
												<label className='field-label'>SWIFT / BIC ID / IBAN</label>
												<Field name='swift_code' className='input' />
												<ErrorMessage name='swift_code' component="div" className="tap-add-experience-text text-danger" />
											</div>
										</div>
									</div>

									<div className="tap-add-experience-2-section mt-6 gap-1">
										<span className="team-admin-add-member__section-title">Address</span>
										<div className='form-field team-admin-add-member__card'>
											<div className="w-100">
												<label className='field-label'>Street Address</label>
												<Field name='street_address' className='input' />
												<ErrorMessage name='street_address' component="div" className="tap-add-experience-text text-danger" />
											</div>
											<div className="w-100">
												<label className='field-label mt-4'>Apt, suite, bldg</label>
												<Field name='apt' className='input' />
												<ErrorMessage name='apt' component="div" className="tap-add-experience-text text-danger" />
											</div>
											<div className="w-100">
												<label className='field-label mt-4'>City</label>
												<CityAutocompleteField name='city' />
												<ErrorMessage name='city' component="div" className="tap-add-experience-text text-danger" />
											</div>
											<div className="w-100 d-flex flex-row gap-6">
												<div className="div-flex-50">
													<label className='field-label mt-4'>State</label>
													<Field name='state' className='input' />
													<ErrorMessage name='state' component="div" className="tap-add-experience-text text-danger" />
												</div>
												<div className="div-flex-50">
													<label className='field-label mt-4'>Zip code</label>
													<Field name='zip_code' className='input' />
													<ErrorMessage name='zip_code' component="div" className="tap-add-experience-text text-danger" />
												</div>
											</div>
										</div>
									</div>

								</div>

								{/* Buttons */}
								<div className='form-actions'>
									<button
										type='button'
										className='btn-secondary'
										onClick={() => { onClose() }}
									>
										Cancel
									</button>
									<button
										type='submit'
										className='btn nb-btn-primary'
										disabled={loading}
										aria-busy={loading ? 'true' : 'false'}
										aria-live='polite'
									>
										{!loading ? (
											<>
												<span className='nb-heading-md'>save changes</span>
												<img
													src='/media/svg/nobilis/vector1.svg'
													alt=''
													className='nb-btn-icon nb-btn-icon--white'
												/>
											</>
										) : (
											<span className='indicator-progress nb-heading-md'>
												Please wait...
												<span
													className='spinner-border spinner-border-sm align-middle ms-2'
													role='status'
													aria-hidden='true'
												/>
											</span>
										)}
									</button>
								</div>

							</Form>
						)
					}}
				</Formik>

			</div>
		</div>
	)
}

export default PaymentMethodModal
